import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, withCtx as _withCtx, createBlock as _createBlock, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-433df8ae"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "title" }
const _hoisted_2 = ["aria-expanded"]
const _hoisted_3 = {
  key: 0,
  class: "title-text truncate"
}
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "spacer" }, null, -1))
const _hoisted_5 = { class: "entry-text truncate" }
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "spacer" }, null, -1))

import { ref, watch, onMounted, nextTick } from "vue";
import {
  useEventListener,
  useMutationObserver,
  onClickOutside,
} from "@vueuse/core";
import AppCheckbox from "./AppCheckbox.vue";
import { firstInView } from "@/util/dom";

type Entries = Array<{
  section: HTMLElement;
  id: string;
  icon: string;
  text: string;
}>;

/** toc entries */

export default /*#__PURE__*/_defineComponent({
  __name: 'TheTableOfContents',
  setup(__props) {

const entries = ref<Entries>([]);
/** whether toc is open or not */
const expanded = ref(window.innerWidth > 1400);
/** how much to push downward to make room for header if in view */
const nudge = ref(0);
/** whether to only show one section at a time */
const oneAtATime = ref(false);
/** active (in view or selected) section */
const active = ref(0);

/** table of contents panel element */
const toc = ref<HTMLElement>();

/** listen for close event */
useEventListener(window, "closetoc", () => (expanded.value = false));

/** update toc position */
async function updatePosition() {
  /** wait for rendering to finish */
  await nextTick();

  /** get dimensions of header and "sub-header" (e.g. first section on node page) */
  const headerEl = document?.querySelector("header");
  const subHeaderEl = document?.querySelector("main > section:first-child");
  if (!headerEl || !subHeaderEl) return;
  const header = headerEl.getBoundingClientRect();
  const subHeader = subHeaderEl.getBoundingClientRect();

  /** calculate nudge */
  nudge.value = Math.max(
    header.top + header.height,
    subHeader.top + subHeader.height
  );

  /** find in view section */
  if (!oneAtATime.value)
    active.value = firstInView(entries.value.map(({ section }) => section));
}

/** update toc entries */
function updateEntries() {
  entries.value = Array.from(
    /** get all headings except top level one */
    document?.querySelectorAll("h2[id], h3[id]") || []
  ).map((element) =>
    /** get relevant props from heading */
    ({
      section: (element.closest("section") as HTMLElement) || null,
      id: element.getAttribute("id") || "",
      icon:
        element.querySelector("[data-icon]")?.getAttribute("data-icon") || "",
      text: (element as HTMLElement).innerText || "",
    })
  );
}

/** hide/show sections based on active */
function hideShow() {
  for (const [index, { section }] of Object.entries(entries.value))
    if (section)
      section.style.display =
        active.value === Number(index) || !oneAtATime.value ? "" : "none";
}

/** when user clicks "off" of toc panel */
onClickOutside(toc, () => {
  if (expanded.value && window.innerWidth < 1240) expanded.value = false;
});

watch(oneAtATime, hideShow);

watch(active, hideShow);

/** run update on: page load, scroll, resize, reflow, etc. */
onMounted(updateEntries);
onMounted(updatePosition);
useEventListener(window, "scroll", updatePosition);
useEventListener(window, "resize", updatePosition);
useMutationObserver(
  document?.body,
  () => {
    updatePosition();
    updateEntries();
  },
  {
    subtree: true,
    childList: true,
  }
);

return (_ctx: any,_cache: any) => {
  const _component_AppIcon = _resolveComponent("AppIcon")!
  const _component_AppLink = _resolveComponent("AppLink")!
  const _component_AppFlex = _resolveComponent("AppFlex")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createBlock(_component_AppFlex, {
    ref_key: "toc",
    ref: toc,
    flow: "inline",
    direction: "col",
    gap: "none",
    "h-align": "stretch",
    class: "toc",
    style: _normalizeStyle({ top: nudge.value + 'px' }),
    "data-expanded": expanded.value,
    role: "doc-toc",
    "aria-label": "Page table of contents",
    onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _withDirectives((_openBlock(), _createElementBlock("button", {
          class: "title-button",
          "aria-expanded": expanded.value,
          onClick: _cache[0] || (_cache[0] = ($event: any) => (expanded.value = !expanded.value))
        }, [
          _createVNode(_component_AppIcon, {
            icon: expanded.value ? 'times' : 'bars'
          }, null, 8, ["icon"])
        ], 8, _hoisted_2)), [
          [_directive_tooltip, 
          expanded.value ? 'Close table of contents' : 'Expand table of contents'
        ]
        ]),
        (expanded.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_3, "Table of Contents"))
          : _createCommentVNode("", true)
      ]),
      (expanded.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _hoisted_4,
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(entries.value, (entry, index) => {
              return (_openBlock(), _createBlock(_component_AppLink, {
                key: index,
                to: '#' + entry.id,
                class: "entry",
                "data-active": active.value === index,
                "aria-current": active.value === index,
                onClick: ($event: any) => (active.value = index)
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_AppIcon, {
                    icon: entry.icon,
                    class: "entry-icon"
                  }, null, 8, ["icon"]),
                  _createElementVNode("span", _hoisted_5, _toDisplayString(entry.text), 1)
                ]),
                _: 2
              }, 1032, ["to", "data-active", "aria-current", "onClick"]))
            }), 128)),
            _hoisted_6,
            _withDirectives(_createVNode(AppCheckbox, {
              modelValue: oneAtATime.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((oneAtATime).value = $event)),
              text: "Show single section"
            }, null, 8, ["modelValue"]), [
              [_directive_tooltip, 'Only show one section at a time']
            ])
          ], 64))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["style", "data-expanded"]))
}
}

})